import type {
  IntelTechnologiesSelectDto,
  MeiaCatalogInvestigativeQuestionTemplatesSelectDto,
  MeiaCatalogKopTemplatesSelectDto,
  MeiaCatalogMissionPurposeTemplatesSelectDto,
  MeiaCatalogMoeTemplatesSelectDto,
  MeiaCatalogMopTemplatesSelectDto,
  MeiaCatalogMosTemplatesSelectDto,
  MeiaCatalogRedForceActivitiesSelectDto,
  MeiaCatalogRedForceActivitySelectDto,
  MeiaCatalogStakeholderTemplatesSelectDto,
  MeiaCatalogThreatTargetSystemSelectDto,
  MeiaCatalogThreatTargetSystemsSelectDto,
  MeiaSearchResultsSelectDto,
  MissionListSelectDto,
  MissionsListSelectDto,
  StudiesSelectDto,
  StudyAcrEntriesSelectDto,
  StudyAcrEntryCreateDto,
  StudyAcrEntrySelectDto,
  StudyAcrEntryUpdateDto,
  StudyArchitectureAlternativeSelectDto,
  StudyArchitectureAlternativesSelectDto,
  StudyBriefAnalysisSelectDto,
  StudyCostEffectivenessResultSelectDto,
  StudyCostEffectivenessResultsSelectDto,
  StudyCreateDto,
  StudyGateReadinessSelectDto,
  StudyInvestigativeQuestionCreateDto,
  StudyInvestigativeQuestionSelectDto,
  StudyInvestigativeQuestionUpdateDto,
  StudyInvestigativeQuestionsSelectDto,
  StudyListSelectDto,
  StudyMissionCreateDto,
  StudyMissionRedActivityBindingCreateDto,
  StudyMissionRedActivityBindingSelectDto,
  StudyMissionRedActivityBindingUpdateDto,
  StudyMissionRedActivityBindingsSelectDto,
  StudyMissionSelectDto,
  StudyMissionStatusTransitionDto,
  StudyMissionUpdateDto,
  StudyMissionsSelectDto,
  StudyMoeCreateDto,
  StudyMoeEntriesSelectDto,
  StudyMoeSelectDto,
  StudyMoeUpdateDto,
  StudyMopCreateDto,
  StudyMopEntriesSelectDto,
  StudyMopSelectDto,
  StudyMopUpdateDto,
  StudyMosCreateDto,
  StudyMosEntriesSelectDto,
  StudyMosSelectDto,
  StudyMosUpdateDto,
  StudyP4E2ERowSelectDto,
  StudyP4E2ERowsSelectDto,
  StudyRunCaseCreateDto,
  StudyRunCaseDashboardSelectDto,
  StudyRunCaseSelectDto,
  StudyRunCaseUpdateDto,
  StudyRunCasesSelectDto,
  StudyRunExecutionSelectDto,
  StudyScoringOutputSelectDto,
  StudyScoringOutputsSelectDto,
  StudySelectDto,
  StudySensitivityResultSelectDto,
  StudySensitivityResultsSelectDto,
  StudySignoffAttemptDto,
  StudySignoffAttemptSelectDto,
  StudySignoffSelectDto,
  StudySignoffsSelectDto,
  StudyStakeholderCandidatesSelectDto,
  StudyStakeholderCreateDto,
  StudyStakeholderSelectDto,
  StudyStakeholderUpdateDto,
  StudyStakeholderUserSelectDto,
  StudyStakeholdersSelectDto,
  StudySwimlaneCreateDto,
  StudySwimlaneSelectDto,
  StudySwimlaneUpdateDto,
  StudySwimlanesSelectDto,
  StudyTargetEffectOverrideClearDto,
  StudyTargetEffectOverrideSelectDto,
  StudyTargetEffectOverrideUpsertDto,
  StudyTargetEffectOverridesSelectDto,
  StudyTaskCreateDto,
  StudyTaskMoeSelectDto,
  StudyTaskMoesSelectDto,
  StudyTaskMopSelectDto,
  StudyTaskMopsSelectDto,
  StudyTaskMosSelectDto,
  StudyTaskMosesSelectDto,
  StudyTaskReadinessListSelectDto,
  StudyTaskReadinessSelectDto,
  StudyTaskSelectDto,
  StudyTaskTargetEngagementCreateDto,
  StudyTaskTargetEngagementSelectDto,
  StudyTaskTargetEngagementUpdateDto,
  StudyTaskTargetEngagementsSelectDto,
  StudyTaskTechnologiesSelectDto,
  StudyTaskTechnologyCreateDto,
  StudyTaskTechnologySelectDto,
  StudyTaskUpdateDto,
  StudyTasksSelectDto,
  StudyThreadCreateDto,
  StudyThreadSelectDto,
  StudyThreadUpdateDto,
  StudyThreadsSelectDto,
  StudyThreatTargetSelectDto,
  StudyThreatTargetsSelectDto,
  StudyUpdateDto,
  StudyValidationResultDto,
  StudyValidationResultsSelectDto,
  WorkflowGateAuditSelectDto,
  WorkflowGateReadinessSelectDto,
} from '@@types/api.types';
import { StudyGate } from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { MEIA_SEARCH_MIN_QUERY_LENGTH } from '@components/pages/meia/meia.constants';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import {
  type QueryClient,
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

const invalidateMeiaGateQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: ['meia', 'getStudyGateAudit'] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getStudyGateReadiness'] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getStudy'] });
  queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
};

const invalidateMeiaThreadTaskReadiness = (queryClient: QueryClient): void => {
  // Task readiness is keyed by threadId; mutations don't always know which thread, so
  // refresh all keys under this prefix.
  queryClient.invalidateQueries({ queryKey: ['meia', 'getThreadTaskReadiness'] });
};

const invalidateMeiaStageMeasureQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: ['meia', 'getSwimlaneMosEntries'] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getThreadSwimlanes'] });
};

const invalidateMeiaRunCaseQueries = (queryClient: QueryClient, studyId: string): void => {
  queryClient.invalidateQueries({ queryKey: ['meia', 'getRunCaseBriefAnalysis', studyId] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getRunCases', studyId] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getRunCaseDashboard', studyId] });
  queryClient.invalidateQueries({ queryKey: ['meia', 'getScoringOutputs', studyId] });
  queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyValidationResults(studyId) });
  invalidateMeiaGateQueries(queryClient);
};

const upsertStudySignoffQueryData = (queryClient: QueryClient, signoff: StudySignoffSelectDto): void => {
  queryClient.setQueryData<StudySignoffSelectDto[]>(
    queryKeys.meia.getStudySignoffs(signoff.studyId),
    (current = []) => {
      const nextSignoffs = current.filter((currentSignoff) => currentSignoff.id !== signoff.id);
      return [signoff, ...nextSignoffs];
    },
  );
};

export const useCreateMeiaStudy = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (study: StudySelectDto): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
    queryClient.setQueryData(queryKeys.meia.getStudy(study.id), study);
  };

  return useMutation<StudySelectDto, Error, StudyCreateDto>({
    mutationFn: async (formData) => {
      return await apiPost<StudySelectDto>({
        body: formData,
        path: '/v1/intel/studies',
      });
    },
    onSuccess,
  });
};

export const useUpdateMeiaStudy = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudySelectDto, Error, { studyId: string } & StudyUpdateDto>({
    mutationFn: async ({ studyId, ...formData }) => {
      return await apiPatch<StudySelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}`,
      });
    },
    onSuccess: (study, variables) => {
      const { studyId, ...patch } = variables;
      const nextStudy = { ...study, ...patch };

      queryClient.setQueryData(queryKeys.meia.getStudy(studyId), nextStudy);
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useArchiveMeiaStudy = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { studyId: string }>({
    mutationFn: async ({ studyId }) => {
      await apiPost<void>({ path: `/v1/intel/studies/${studyId}/archive` });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

const toQueryString = (params: Record<string, string | undefined>): string => {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) queryParams.set(key, value);
  }
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

export type MeiaMissionMeasureFlags = {
  hasMoe: boolean;
  hasMos: boolean;
  hasMop: boolean;
};

export type MeiaStudyMissionMeasuresResult = {
  isError: boolean;
  isLoading: boolean;
  measureFlagsByMissionId: Map<string, MeiaMissionMeasureFlags>;
};

export type MeiaStudyMissionThreadsResult = {
  isError: boolean;
  isLoading: boolean;
  threads: StudyThreadSelectDto[];
};

export type MeiaStudySwimlaneTasksResult = {
  isLoading: boolean;
  tasks: StudyTaskSelectDto[];
};

export type MeiaStudyThreadSwimlanesResult = {
  isError: boolean;
  isLoading: boolean;
  swimlanes: StudySwimlaneSelectDto[];
};

export type MeiaStudyGateEntry = {
  gate: StudyGate;
  isError: boolean;
  isLoading: boolean;
  readiness?: StudyGateReadinessSelectDto;
};

export type MeiaStudyGatesResult = {
  gates: MeiaStudyGateEntry[];
  isLoading: boolean;
};

export type MeiaStudyThreadTaskReadinessResult = {
  isError: boolean;
  isLoading: boolean;
  taskReadiness: Array<StudyTaskReadinessSelectDto & { threadId: string }>;
};

export type MeiaTaskTechnologiesEngagementsResult = {
  engagements: StudyTaskTargetEngagementSelectDto[];
  isError: boolean;
  isLoading: boolean;
};

export type MeiaTasksTechnologiesResult = {
  isError: boolean;
  isLoading: boolean;
  taskTechnologies: StudyTaskTechnologySelectDto[];
};

export const useGetMeiaGateReadiness = (params: { gate: string; missionId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { gate, missionId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<WorkflowGateReadinessSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<WorkflowGateReadinessSelectDto>({
        path: `/v1/intel/studies/${studyId}/gate/${gate}/status${toQueryString({ missionId })}`,
      });
    },
    queryKey:
      enabled && studyId
        ? queryKeys.meia.getStudyGateReadiness({ gate, missionId, studyId })
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaGateAudit = (params: { gate: string; missionId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { gate, missionId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<WorkflowGateAuditSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<WorkflowGateAuditSelectDto>({
        path: `/v1/intel/studies/${studyId}/gate/${gate}/audit${toQueryString({ missionId })}`,
      });
    },
    queryKey:
      enabled && studyId
        ? queryKeys.meia.getStudyGateAudit({ gate, missionId, studyId })
        : queryKeys.placeholders.default,
  });
};

const meiaStudyGateOrder = [StudyGate.G1, StudyGate.G2, StudyGate.G3, StudyGate.G4];

export const useGetMeiaStudyGates = (params: { studyId?: string }): MeiaStudyGatesResult => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  const queries = useQueries({
    queries: meiaStudyGateOrder.map((gate) => ({
      enabled,
      queryFn: async () => {
        return await apiGet<StudyGateReadinessSelectDto>({
          path: `/v1/intel/studies/${studyId}/gate/${gate}/readiness`,
        });
      },
      queryKey:
        enabled && studyId
          ? queryKeys.meia.getStudyGateSignoffReadiness({ gate, studyId })
          : queryKeys.placeholders.default,
    })),
  });

  return {
    gates: meiaStudyGateOrder.map((gate, index) => ({
      gate,
      isError: queries[index]?.isError ?? false,
      isLoading: queries[index]?.isLoading ?? false,
      readiness: queries[index]?.data,
    })),
    isLoading: queries.some((query) => query.isLoading),
  };
};

export const useGetMeiaStudies = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<StudyListSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudiesSelectDto>({
        path: '/v1/intel/studies',
      });
      return data.studies;
    },
    queryKey: enabled ? queryKeys.meia.getStudies : queryKeys.placeholders.default,
  });
};

export const useGetMeiaSearch = (params: { q: string }) => {
  // Passive, debounced ⌘K search — failures surface as the palette's empty state, not a toast.
  const { apiGet } = useApi({ showToastOnError: false });
  const { q } = params;
  const term = q.trim();
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && term.length >= MEIA_SEARCH_MIN_QUERY_LENGTH);

  return useQuery<MeiaSearchResultsSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<MeiaSearchResultsSelectDto>({
        path: '/v1/intel/meia/search',
        query: { q: term },
      });
    },
    queryKey: enabled ? queryKeys.meia.getMeiaSearch(term) : queryKeys.placeholders.default,
    // Repeated identical terms (re-opening the palette, remounting) serve from cache.
    staleTime: 30_000,
  });
};

export const useGetIntelMissions = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MissionListSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<MissionsListSelectDto>({
        path: '/v1/intel/missions',
      });
      return data.missions;
    },
    queryKey: enabled ? queryKeys.meia.getMissions : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudy = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudySelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<StudySelectDto>({
        path: `/v1/intel/studies/${studyId}`,
      });
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudy(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyMissions = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyMissionSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMissionsSelectDto>({
        path: `/v1/intel/studies/${studyId}/missions`,
      });
      return data.missions;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudyMissions(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyArchitectureAlternatives = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyArchitectureAlternativeSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyArchitectureAlternativesSelectDto>({
        path: `/v1/intel/studies/${studyId}/architecture-alternatives`,
      });
      return data.architectureAlternatives;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getArchitectureAlternatives(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaAcrRegister = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyAcrEntrySelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyAcrEntriesSelectDto>({
        path: `/v1/intel/studies/${studyId}/governance/acr-entries`,
      });
      return data.acrEntries;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getAcrRegister(studyId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaAcrEntry = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyAcrEntrySelectDto, Error, { studyId: string } & StudyAcrEntryCreateDto>({
    mutationFn: async ({ studyId, ...body }) => {
      return await apiPost<StudyAcrEntrySelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/governance/acr-entries`,
      });
    },
    onSuccess: (_acrEntry, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getAcrRegister(variables.studyId) });
      invalidateMeiaRunCaseQueries(queryClient, variables.studyId);
    },
  });
};

export const useUpdateMeiaAcrEntry = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyAcrEntrySelectDto, Error, { acrEntryId: string; studyId: string } & StudyAcrEntryUpdateDto>({
    mutationFn: async ({ acrEntryId, studyId, ...body }) => {
      return await apiPatch<StudyAcrEntrySelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/governance/acr-entries/${acrEntryId}`,
      });
    },
    onSuccess: (_acrEntry, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getAcrRegister(variables.studyId) });
      invalidateMeiaRunCaseQueries(queryClient, variables.studyId);
    },
  });
};

export const useDeleteMeiaAcrEntry = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { acrEntryId: string; studyId: string }>({
    mutationFn: async ({ acrEntryId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/governance/acr-entries/${acrEntryId}`,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getAcrRegister(variables.studyId) });
      invalidateMeiaRunCaseQueries(queryClient, variables.studyId);
    },
  });
};

export const useGetMeiaStudyThreatTargets = (params: { enabled?: boolean; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: isEnabled = true, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && studyId);

  return useQuery<StudyThreatTargetSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyThreatTargetsSelectDto>({
        path: `/v1/intel/studies/${studyId}/mission/threat-targets`,
      });
      return data.threatTargets;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudyThreatTargets(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyP4E2eRows = (params: { enabled?: boolean; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: isEnabled = true, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && studyId);

  return useQuery<StudyP4E2ERowSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyP4E2ERowsSelectDto>({
        path: `/v1/intel/studies/${studyId}/task-technologies`,
      });
      return data.rows;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudyP4E2eRows(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaRunCases = (params: { missionId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { missionId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyRunCaseSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyRunCasesSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases${toQueryString({ missionId })}`,
      });
      return data.runCases;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getRunCases({ missionId, studyId }) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaRunCase = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyRunCaseSelectDto, Error, { studyId: string } & StudyRunCaseCreateDto>({
    mutationFn: async ({ studyId, ...formData }) => {
      return await apiPost<StudyRunCaseSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/run-cases`,
      });
    },
    onSuccess: (runCase) => {
      invalidateMeiaRunCaseQueries(queryClient, runCase.studyId);
    },
  });
};

export const useUpdateMeiaRunCase = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyRunCaseSelectDto, Error, { runCaseId: string; studyId: string } & StudyRunCaseUpdateDto>({
    mutationFn: async ({ runCaseId, studyId, ...formData }) => {
      return await apiPatch<StudyRunCaseSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}`,
      });
    },
    onSuccess: (runCase) => {
      invalidateMeiaRunCaseQueries(queryClient, runCase.studyId);
    },
  });
};

export const useDeleteMeiaRunCase = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { runCaseId: string; studyId: string }>({
    mutationFn: async ({ runCaseId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}`,
      });
    },
    onSuccess: (_data, variables) => {
      invalidateMeiaRunCaseQueries(queryClient, variables.studyId);
    },
  });
};

export const useRunMeiaRunCase = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyRunExecutionSelectDto, Error, { runCaseId: string; studyId: string }>({
    mutationFn: async ({ runCaseId, studyId }) => {
      return await apiPost<StudyRunExecutionSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}/run`,
      });
    },
    onSuccess: (execution) => {
      invalidateMeiaRunCaseQueries(queryClient, execution.runCase.studyId);
    },
  });
};

export const useGetMeiaRunCaseDashboard = (params: { enabled?: boolean; runCaseId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: isEnabled = true, runCaseId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && runCaseId && studyId);

  return useQuery<StudyRunCaseDashboardSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<StudyRunCaseDashboardSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}/dashboard`,
      });
    },
    queryKey:
      enabled && runCaseId && studyId
        ? queryKeys.meia.getRunCaseDashboard({ runCaseId, studyId })
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaRunCaseBriefAnalysis = (params: {
  enabled?: boolean;
  missionId?: string;
  runCaseId?: string;
  studyId?: string;
  threadId?: string;
}) => {
  // Passive background fetch that re-fires while navigating missions/threads — failures
  // surface through the Brief analysis tile's empty state instead of an error toast.
  const { apiGet } = useApi({ showToastOnError: false });
  const { enabled: isEnabled = true, missionId, runCaseId, studyId, threadId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && runCaseId && studyId);

  return useQuery<StudyBriefAnalysisSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<StudyBriefAnalysisSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}/brief-analysis`,
        query: {
          ...(missionId ? { missionId } : {}),
          ...(threadId ? { threadId } : {}),
        },
      });
    },
    queryKey:
      enabled && runCaseId && studyId
        ? queryKeys.meia.getRunCaseBriefAnalysis({ missionId, runCaseId, studyId, threadId })
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaRunCaseCostEffectivenessResults = (params: { runCaseId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { runCaseId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && runCaseId && studyId);

  return useQuery<StudyCostEffectivenessResultSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyCostEffectivenessResultsSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}/cost-effectiveness-results`,
      });
      return data.costEffectivenessResults;
    },
    queryKey:
      enabled && runCaseId && studyId
        ? queryKeys.meia.getRunCaseCostEffectivenessResults({ runCaseId, studyId })
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaRunCaseSensitivityResults = (params: { runCaseId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { runCaseId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && runCaseId && studyId);

  return useQuery<StudySensitivityResultSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudySensitivityResultsSelectDto>({
        path: `/v1/intel/studies/${studyId}/run-cases/${runCaseId}/sensitivity-results`,
      });
      return data.sensitivityResults;
    },
    queryKey:
      enabled && runCaseId && studyId
        ? queryKeys.meia.getRunCaseSensitivityResults({ runCaseId, studyId })
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaScoringOutputs = (params: { enabled?: boolean; runCaseId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: isEnabled = true, runCaseId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && studyId);

  return useQuery<StudyScoringOutputSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyScoringOutputsSelectDto>({
        path: `/v1/intel/studies/${studyId}/scoring-outputs`,
        query: runCaseId ? { runCaseId } : undefined,
      });
      return data.scoringOutputs;
    },
    queryKey:
      enabled && studyId ? queryKeys.meia.getScoringOutputs({ runCaseId, studyId }) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyValidationResults = (params: { enabled?: boolean; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: isEnabled = true, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isEnabled && isAuthenticated && studyId);

  return useQuery<StudyValidationResultDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyValidationResultsSelectDto>({
        path: `/v1/intel/studies/${studyId}/validate`,
      });
      return data.validationResults;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudyValidationResults(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudySignoffs = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudySignoffSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudySignoffsSelectDto>({
        path: `/v1/intel/studies/${studyId}/signoffs`,
      });
      return data.signoffs;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudySignoffs(studyId) : queryKeys.placeholders.default,
  });
};

export const useAttemptMeiaStudySignoff = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudySignoffAttemptSelectDto, Error, { gate: string; studyId: string } & StudySignoffAttemptDto>({
    mutationFn: async ({ gate, studyId, ...formData }) => {
      return await apiPost<StudySignoffAttemptSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/gate/${gate}/attempt-signoff`,
      });
    },
    onSuccess: (data, variables) => {
      upsertStudySignoffQueryData(queryClient, data.signoff);
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudySignoffs(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useRevokeMeiaStudySignoff = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudySignoffSelectDto, Error, { gate: string; studyId: string }>({
    mutationFn: async ({ gate, studyId }) => {
      return await apiPost<StudySignoffSelectDto>({
        path: `/v1/intel/studies/${studyId}/gate/${gate}/revoke-signoff`,
      });
    },
    onSuccess: (signoff, variables) => {
      upsertStudySignoffQueryData(queryClient, signoff);
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudies });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudySignoffs(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaInvestigativeQuestionTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogInvestigativeQuestionTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogInvestigativeQuestionTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/investigative-question-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getInvestigativeQuestionTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaKopTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogKopTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogKopTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/kop-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getKopTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaMissionPurposeTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogMissionPurposeTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogMissionPurposeTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/mission-purpose-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getMissionPurposeTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaMoeTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogMoeTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogMoeTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/moe-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getMoeTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaMopTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogMopTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogMopTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/mop-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getMopTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaMosTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogMosTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogMosTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/mos-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getMosTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaRedForceActivities = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogRedForceActivitySelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<MeiaCatalogRedForceActivitiesSelectDto>({
        path: '/v1/intel/meia/catalogs/red-force-activities',
      });
      return data.redForceActivities;
    },
    queryKey: enabled ? queryKeys.meia.getRedForceActivities : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStakeholderTemplates = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogStakeholderTemplatesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<MeiaCatalogStakeholderTemplatesSelectDto>({
        path: '/v1/intel/meia/catalogs/stakeholder-templates',
      });
    },
    queryKey: enabled ? queryKeys.meia.getStakeholderTemplates : queryKeys.placeholders.default,
  });
};

export const useGetMeiaThreatTargetSystems = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MeiaCatalogThreatTargetSystemSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<MeiaCatalogThreatTargetSystemsSelectDto>({
        path: '/v1/intel/meia/catalogs/threat-target-systems',
      });
      return data.threatTargetSystems;
    },
    queryKey: enabled ? queryKeys.meia.getThreatTargetSystems : queryKeys.placeholders.default,
  });
};

export const useGetMeiaMissionRedActivityBindings = (params: { missionId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { missionId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && missionId && studyId);

  return useQuery<StudyMissionRedActivityBindingSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMissionRedActivityBindingsSelectDto>({
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/red-force-activity-bindings`,
      });
      return data.redActivityBindings;
    },
    queryKey:
      enabled && missionId ? queryKeys.meia.getMissionRedActivityBindings(missionId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaStudyMission = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (mission: StudyMissionSelectDto): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(mission.studyId) });
    invalidateMeiaGateQueries(queryClient);
  };

  return useMutation<StudyMissionSelectDto, Error, { studyId: string } & StudyMissionCreateDto>({
    mutationFn: async ({ studyId, ...formData }) => {
      return await apiPost<StudyMissionSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/missions`,
      });
    },
    onSuccess,
  });
};

export const useUpdateMeiaStudyMission = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (mission: StudyMissionSelectDto): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(mission.studyId) });
    invalidateMeiaGateQueries(queryClient);
  };

  return useMutation<StudyMissionSelectDto, Error, { missionId: string; studyId: string } & StudyMissionUpdateDto>({
    mutationFn: async ({ missionId, studyId, ...formData }) => {
      return await apiPatch<StudyMissionSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/missions/${missionId}`,
      });
    },
    onSuccess,
  });
};

export const useDeleteMeiaStudyMission = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { missionId: string; studyId: string }>({
    mutationFn: async ({ missionId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/missions/${missionId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useCreateMeiaMissionRedActivityBinding = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyMissionRedActivityBindingSelectDto,
    Error,
    { missionId: string; studyId: string } & StudyMissionRedActivityBindingCreateDto
  >({
    mutationFn: async ({ missionId, studyId, ...body }) => {
      return await apiPost<StudyMissionRedActivityBindingSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/red-force-activity-bindings`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionRedActivityBindings(variables.missionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaMissionRedActivityBinding = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyMissionRedActivityBindingSelectDto,
    Error,
    { bindingId: string; missionId: string; studyId: string } & StudyMissionRedActivityBindingUpdateDto
  >({
    mutationFn: async ({ bindingId, missionId, studyId, ...body }) => {
      return await apiPatch<StudyMissionRedActivityBindingSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/red-force-activity-bindings/${bindingId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionRedActivityBindings(variables.missionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaMissionRedActivityBinding = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { bindingId: string; missionId: string; studyId: string }>({
    mutationFn: async ({ bindingId, missionId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/red-force-activity-bindings/${bindingId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionRedActivityBindings(variables.missionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useTransitionMeiaStudyMissionStatus = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyMissionSelectDto,
    Error,
    { missionId: string; studyId: string } & StudyMissionStatusTransitionDto
  >({
    mutationFn: async ({ missionId, studyId, ...body }) => {
      return await apiPatch<StudyMissionSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/status`,
      });
    },
    onSuccess: (mission, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionRedActivityBindings(mission.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyMissions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaStudyStakeholders = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyStakeholderSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyStakeholdersSelectDto>({
        path: `/v1/intel/studies/${studyId}/stakeholders`,
      });
      return data.stakeholders;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getStudyStakeholders(studyId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyStakeholderCandidates = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyStakeholderUserSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyStakeholderCandidatesSelectDto>({
        path: `/v1/intel/studies/${studyId}/stakeholders/candidates`,
      });
      return data.users;
    },
    queryKey:
      enabled && studyId ? queryKeys.meia.getStudyStakeholderCandidates(studyId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaStudyStakeholder = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyStakeholderSelectDto, Error, { studyId: string } & StudyStakeholderCreateDto>({
    mutationFn: async ({ studyId, ...formData }) => {
      return await apiPost<StudyStakeholderSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/stakeholders`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyStakeholders(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaStudyStakeholder = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyStakeholderSelectDto,
    Error,
    { stakeholderId: string; studyId: string } & StudyStakeholderUpdateDto
  >({
    mutationFn: async ({ stakeholderId, studyId, ...formData }) => {
      return await apiPatch<StudyStakeholderSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/stakeholders/${stakeholderId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyStakeholders(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaStudyStakeholder = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { stakeholderId: string; studyId: string }>({
    mutationFn: async ({ stakeholderId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/stakeholders/${stakeholderId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyStakeholders(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaStudyInvestigativeQuestions = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyInvestigativeQuestionSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyInvestigativeQuestionsSelectDto>({
        path: `/v1/intel/studies/${studyId}/investigative-questions`,
      });
      return data.investigativeQuestions;
    },
    queryKey:
      enabled && studyId ? queryKeys.meia.getStudyInvestigativeQuestions(studyId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaStudyInvestigativeQuestion = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyInvestigativeQuestionSelectDto,
    Error,
    { studyId: string } & StudyInvestigativeQuestionCreateDto
  >({
    mutationFn: async ({ studyId, ...formData }) => {
      return await apiPost<StudyInvestigativeQuestionSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/investigative-questions`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyInvestigativeQuestions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaStudyInvestigativeQuestion = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyInvestigativeQuestionSelectDto,
    Error,
    { questionId: string; studyId: string } & StudyInvestigativeQuestionUpdateDto
  >({
    mutationFn: async ({ questionId, studyId, ...formData }) => {
      return await apiPatch<StudyInvestigativeQuestionSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/investigative-questions/${questionId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyInvestigativeQuestions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaStudyInvestigativeQuestion = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { questionId: string; studyId: string }>({
    mutationFn: async ({ questionId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/investigative-questions/${questionId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyInvestigativeQuestions(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaMosEntries = (params: { missionId?: string }) => {
  const { apiGet } = useApi();
  const { missionId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && missionId);

  return useQuery<StudyMosSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMosEntriesSelectDto>({
        path: `/v1/intel/missions/${missionId}/mos-entries`,
      });
      return data.mosEntries;
    },
    queryKey: enabled && missionId ? queryKeys.meia.getMosEntries(missionId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaMosEntry = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMosSelectDto, Error, { missionId: string } & StudyMosCreateDto>({
    mutationFn: async ({ missionId, ...formData }) => {
      return await apiPost<StudyMosSelectDto>({
        body: formData,
        path: `/v1/intel/missions/${missionId}/mos-entries`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMosEntries(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaMosEntry = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMosSelectDto, Error, { missionId: string; mosId: string } & StudyMosUpdateDto>({
    mutationFn: async ({ missionId, mosId, ...formData }) => {
      return await apiPatch<StudyMosSelectDto>({
        body: formData,
        path: `/v1/intel/missions/${missionId}/mos-entries/${mosId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMosEntries(variables.missionId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaMosEntry = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { missionId: string; mosId: string }>({
    mutationFn: async ({ missionId, mosId }) => {
      await apiDelete<void>({
        path: `/v1/intel/missions/${missionId}/mos-entries/${mosId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMosEntries(variables.missionId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaMoeEntries = (params: { mosId?: string }) => {
  const { apiGet } = useApi();
  const { mosId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && mosId);

  return useQuery<StudyMoeSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMoeEntriesSelectDto>({
        path: `/v1/intel/mos-entries/${mosId}/moe-entries`,
      });
      return data.moeEntries;
    },
    queryKey: enabled && mosId ? queryKeys.meia.getMoeEntries(mosId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaMoeEntry = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMoeSelectDto, Error, { mosId: string } & StudyMoeCreateDto>({
    mutationFn: async ({ mosId, ...formData }) => {
      return await apiPost<StudyMoeSelectDto>({
        body: formData,
        path: `/v1/intel/mos-entries/${mosId}/moe-entries`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMoeEntries(variables.mosId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaMoeEntry = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMoeSelectDto, Error, { moeId: string; mosId: string } & StudyMoeUpdateDto>({
    mutationFn: async ({ moeId, mosId, ...formData }) => {
      return await apiPatch<StudyMoeSelectDto>({
        body: formData,
        path: `/v1/intel/mos-entries/${mosId}/moe-entries/${moeId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMoeEntries(variables.mosId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaMoeEntry = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { moeId: string; mosId: string }>({
    mutationFn: async ({ moeId, mosId }) => {
      await apiDelete<void>({
        path: `/v1/intel/mos-entries/${mosId}/moe-entries/${moeId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMoeEntries(variables.mosId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useRebalanceMeiaMoeWeights = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMoeSelectDto[], Error, { mosId: string }>({
    mutationFn: async ({ mosId }) => {
      const data = await apiPost<StudyMoeEntriesSelectDto>({
        path: `/v1/intel/mos-entries/${mosId}/moe-entries/rebalance-weights`,
      });
      return data.moeEntries;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMoeEntries(variables.mosId) });
      invalidateMeiaStageMeasureQueries(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaMopEntries = (params: { missionId?: string }) => {
  const { apiGet } = useApi();
  const { missionId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && missionId);

  return useQuery<StudyMopSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMopEntriesSelectDto>({
        path: `/v1/intel/missions/${missionId}/mop-entries`,
      });
      return data.mopEntries;
    },
    queryKey: enabled && missionId ? queryKeys.meia.getMopEntries(missionId) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaMopEntry = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMopSelectDto, Error, { missionId: string } & StudyMopCreateDto>({
    mutationFn: async ({ missionId, ...formData }) => {
      return await apiPost<StudyMopSelectDto>({
        body: formData,
        path: `/v1/intel/missions/${missionId}/mop-entries`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMopEntries(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaMopEntry = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMopSelectDto, Error, { missionId: string; mopId: string } & StudyMopUpdateDto>({
    mutationFn: async ({ missionId, mopId, ...formData }) => {
      return await apiPatch<StudyMopSelectDto>({
        body: formData,
        path: `/v1/intel/missions/${missionId}/mop-entries/${mopId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMopEntries(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaMopEntry = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { missionId: string; mopId: string }>({
    mutationFn: async ({ missionId, mopId }) => {
      await apiDelete<void>({
        path: `/v1/intel/missions/${missionId}/mop-entries/${mopId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMopEntries(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

// Per-mission MoS/MoP queries plus per-MoS MoE queries, reduced to the boolean
// flags the P1 progress formula needs. Reuses the single-entity query keys so
// measure mutations keep these results fresh.
export const useGetMeiaStudyMissionMeasures = (params: {
  missions: StudyMissionSelectDto[];
}): MeiaStudyMissionMeasuresResult => {
  const { apiGet } = useApi();
  const { missions } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  const mosQueries = useQueries({
    queries: missions.map((mission) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMosEntriesSelectDto>({
          path: `/v1/intel/missions/${mission.id}/mos-entries`,
        });
        return data.mosEntries;
      },
      queryKey: enabled ? queryKeys.meia.getMosEntries(mission.id) : queryKeys.placeholders.default,
    })),
  });

  const mopQueries = useQueries({
    queries: missions.map((mission) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMopEntriesSelectDto>({
          path: `/v1/intel/missions/${mission.id}/mop-entries`,
        });
        return data.mopEntries;
      },
      queryKey: enabled ? queryKeys.meia.getMopEntries(mission.id) : queryKeys.placeholders.default,
    })),
  });

  const mosEntries = mosQueries.flatMap((query) => query.data ?? []);
  const moeQueries = useQueries({
    queries: mosEntries.map((mos) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMoeEntriesSelectDto>({
          path: `/v1/intel/mos-entries/${mos.id}/moe-entries`,
        });
        return data.moeEntries;
      },
      queryKey: enabled ? queryKeys.meia.getMoeEntries(mos.id) : queryKeys.placeholders.default,
    })),
  });

  const missionIdsWithMoe = new Set(
    moeQueries.flatMap((query, index) => {
      const mos = mosEntries[index];
      if (!mos || (query.data ?? []).length === 0) return [];
      return [mos.missionId];
    }),
  );

  const allQueries = [...mosQueries, ...mopQueries, ...moeQueries];

  return {
    isError: allQueries.some((query) => query.isError),
    isLoading: allQueries.some((query) => query.isLoading),
    measureFlagsByMissionId: new Map(
      missions.map((mission, index) => [
        mission.id,
        {
          hasMoe: missionIdsWithMoe.has(mission.id),
          hasMos: (mosQueries[index]?.data ?? []).length > 0,
          hasMop: (mopQueries[index]?.data ?? []).length > 0,
        },
      ]),
    ),
  };
};

export const useGetMeiaMissionThreads = (params: { missionId?: string; studyId?: string }) => {
  const { apiGet } = useApi();
  const { missionId, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && missionId && studyId);

  return useQuery<StudyThreadSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyThreadsSelectDto>({
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/threads`,
      });
      return data.threads;
    },
    queryKey: enabled && missionId ? queryKeys.meia.getMissionThreads(missionId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyMissionThreads = (params: {
  missions: StudyMissionSelectDto[];
  studyId?: string;
}): MeiaStudyMissionThreadsResult => {
  const { apiGet } = useApi();
  const { missions, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  const queries = useQueries({
    queries: missions.map((mission) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyThreadsSelectDto>({
          path: `/v1/intel/studies/${studyId}/missions/${mission.id}/threads`,
        });
        return data.threads;
      },
      queryKey: enabled ? queryKeys.meia.getMissionThreads(mission.id) : queryKeys.placeholders.default,
    })),
  });

  return {
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    threads: queries.flatMap((query) => query.data ?? []),
  };
};

export const useCreateMeiaMissionThread = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyThreadSelectDto, Error, { missionId: string; studyId: string } & StudyThreadCreateDto>({
    mutationFn: async ({ missionId, studyId, ...formData }) => {
      return await apiPost<StudyThreadSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/missions/${missionId}/threads`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionThreads(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaMissionThread = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyThreadSelectDto,
    Error,
    { missionId: string; studyId: string; threadId: string } & StudyThreadUpdateDto
  >({
    mutationFn: async ({ missionId: _missionId, studyId, threadId, ...formData }) => {
      return await apiPatch<StudyThreadSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/threads/${threadId}`,
      });
    },
    onSuccess: (_, variables) => {
      // Re-seeding (when the kill-chain pattern changes) replaces the thread's swimlanes,
      // so we invalidate the swimlane list here too.
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionThreads(variables.missionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaMissionThread = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { missionId: string; studyId: string; threadId: string }>({
    mutationFn: async ({ studyId, threadId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/threads/${threadId}`,
      });
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) }),
        queryClient.cancelQueries({ queryKey: queryKeys.meia.getThreadTaskReadiness(variables.threadId) }),
      ]);

      queryClient.removeQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) });
      queryClient.removeQueries({ queryKey: queryKeys.meia.getThreadTaskReadiness(variables.threadId) });
      queryClient.setQueryData<StudyThreadSelectDto[]>(
        queryKeys.meia.getMissionThreads(variables.missionId),
        (current = []) => current.filter((thread) => thread.id !== variables.threadId),
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getMissionThreads(variables.missionId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaThreadSwimlanes = (params: { studyId?: string; threadId?: string }) => {
  const { apiGet } = useApi();
  const { studyId, threadId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId && threadId);

  return useQuery<StudySwimlaneSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudySwimlanesSelectDto>({
        path: `/v1/intel/studies/${studyId}/threads/${threadId}/swimlanes`,
      });
      return data.swimlanes;
    },
    queryKey: enabled && threadId ? queryKeys.meia.getThreadSwimlanes(threadId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyThreadSwimlanes = (params: {
  studyId?: string;
  threads: StudyThreadSelectDto[];
}): MeiaStudyThreadSwimlanesResult => {
  const { apiGet } = useApi();
  const { studyId, threads } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  const queries = useQueries({
    queries: threads.map((thread) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudySwimlanesSelectDto>({
          path: `/v1/intel/studies/${studyId}/threads/${thread.id}/swimlanes`,
        });
        return data.swimlanes;
      },
      queryKey: enabled ? queryKeys.meia.getThreadSwimlanes(thread.id) : queryKeys.placeholders.default,
    })),
  });

  return {
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    swimlanes: queries.flatMap((query) => query.data ?? []),
  };
};

export const useCreateMeiaThreadSwimlane = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudySwimlaneSelectDto, Error, { studyId: string; threadId: string } & StudySwimlaneCreateDto>({
    mutationFn: async ({ studyId, threadId, ...formData }) => {
      return await apiPost<StudySwimlaneSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/threads/${threadId}/swimlanes`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaThreadSwimlane = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudySwimlaneSelectDto,
    Error,
    { studyId: string; swimlaneId: string; threadId: string } & StudySwimlaneUpdateDto
  >({
    mutationFn: async ({ studyId, swimlaneId, threadId: _threadId, ...formData }) => {
      return await apiPatch<StudySwimlaneSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/swimlanes/${swimlaneId}`,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneMosEntries(data.id) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaThreadSwimlane = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { studyId: string; swimlaneId: string; threadId: string }>({
    mutationFn: async ({ studyId, swimlaneId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/swimlanes/${swimlaneId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getThreadSwimlanes(variables.threadId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaSwimlaneMosEntries = (params: { swimlaneId?: string }) => {
  const { apiGet } = useApi();
  const { swimlaneId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && swimlaneId);

  return useQuery<StudyMosSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyMosEntriesSelectDto>({
        path: `/v1/intel/swimlanes/${swimlaneId}/mos-entries`,
      });
      return data.mosEntries;
    },
    queryKey: enabled && swimlaneId ? queryKeys.meia.getSwimlaneMosEntries(swimlaneId) : queryKeys.placeholders.default,
  });
};

export type MeiaSwimlaneMosTree = {
  moes: StudyMoeSelectDto[];
  mos: StudyMosSelectDto;
};

export type MeiaSwimlaneMosTreesResult = {
  isLoading: boolean;
  trees: MeiaSwimlaneMosTree[];
};

// Swimlane MoS list plus a per-MoS MoE fan-out, assembled into full trees so the inline
// measures form can hydrate every MoS + MoE in one pass. Reuses the single-entity query
// keys so MoS/MoE mutations keep these results fresh.
export const useGetMeiaSwimlaneMosTrees = (params: { swimlaneId?: string }): MeiaSwimlaneMosTreesResult => {
  const { apiGet } = useApi();
  const { swimlaneId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && swimlaneId);
  const { data: mosEntries = [], isLoading: isMosLoading } = useGetMeiaSwimlaneMosEntries({ swimlaneId });

  const moeQueries = useQueries({
    queries: mosEntries.map((mos) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMoeEntriesSelectDto>({
          path: `/v1/intel/mos-entries/${mos.id}/moe-entries`,
        });
        return data.moeEntries;
      },
      queryKey: enabled ? queryKeys.meia.getMoeEntries(mos.id) : queryKeys.placeholders.default,
    })),
  });

  return {
    isLoading: isMosLoading || moeQueries.some((query) => query.isLoading),
    trees: mosEntries.map((mos, index) => ({ moes: moeQueries[index]?.data ?? [], mos })),
  };
};

export const useCreateMeiaSwimlaneMosEntry = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyMosSelectDto, Error, { swimlaneId: string } & StudyMosCreateDto>({
    mutationFn: async ({ swimlaneId, ...formData }) => {
      return await apiPost<StudyMosSelectDto>({
        body: formData,
        path: `/v1/intel/swimlanes/${swimlaneId}/mos-entries`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneMosEntries(variables.swimlaneId) });
      queryClient.invalidateQueries({ queryKey: ['meia', 'getThreadSwimlanes'] });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaSwimlaneTasks = (params: { studyId?: string; swimlaneId?: string }) => {
  const { apiGet } = useApi();
  const { studyId, swimlaneId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId && swimlaneId);

  return useQuery<StudyTaskSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTasksSelectDto>({
        path: `/v1/intel/studies/${studyId}/swimlanes/${swimlaneId}/tasks`,
      });
      return data.tasks;
    },
    queryKey: enabled && swimlaneId ? queryKeys.meia.getSwimlaneTasks(swimlaneId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudySwimlaneTasks = (params: {
  studyId?: string;
  swimlanes: StudySwimlaneSelectDto[];
}): MeiaStudySwimlaneTasksResult => {
  const { apiGet } = useApi();
  const { studyId, swimlanes } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  const queries = useQueries({
    queries: swimlanes.map((swimlane) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTasksSelectDto>({
          path: `/v1/intel/studies/${studyId}/swimlanes/${swimlane.id}/tasks`,
        });
        return data.tasks;
      },
      queryKey: enabled ? queryKeys.meia.getSwimlaneTasks(swimlane.id) : queryKeys.placeholders.default,
    })),
  });

  return {
    isLoading: queries.some((query) => query.isLoading),
    tasks: queries.flatMap((query) => query.data ?? []),
  };
};

export const useCreateMeiaTask = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<StudyTaskSelectDto, Error, { studyId: string; swimlaneId: string } & StudyTaskCreateDto>({
    mutationFn: async ({ studyId, swimlaneId, ...formData }) => {
      return await apiPost<StudyTaskSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/swimlanes/${swimlaneId}/tasks`,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(variables.swimlaneId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMoes(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMops(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMoses(data.id) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaTask = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyTaskSelectDto,
    Error,
    { previousSwimlaneId?: string; studyId: string; taskId: string } & StudyTaskUpdateDto
  >({
    mutationFn: async ({ previousSwimlaneId: _previous, studyId, taskId, ...formData }) => {
      return await apiPatch<StudyTaskSelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}`,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(data.swimlaneId) });
      if (variables.previousSwimlaneId && variables.previousSwimlaneId !== data.swimlaneId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(variables.previousSwimlaneId) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMoes(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMops(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTaskMoses(variables.taskId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaTask = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { studyId: string; swimlaneId: string; taskId: string }>({
    mutationFn: async ({ studyId, taskId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}`,
      });
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.meia.getTaskMoes(variables.taskId) }),
        queryClient.cancelQueries({ queryKey: queryKeys.meia.getTaskMops(variables.taskId) }),
        queryClient.cancelQueries({ queryKey: queryKeys.meia.getTaskMoses(variables.taskId) }),
      ]);
      queryClient.setQueryData(queryKeys.meia.getTaskMoes(variables.taskId), []);
      queryClient.setQueryData(queryKeys.meia.getTaskMops(variables.taskId), []);
      queryClient.setQueryData(queryKeys.meia.getTaskMoses(variables.taskId), []);
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(variables.swimlaneId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaTaskTechnologies = (params: { enabled?: boolean; studyId?: string; taskId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, taskId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId && taskId);

  return useQuery<StudyTaskTechnologySelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskTechnologiesSelectDto>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/technologies`,
      });
      return data.taskTechnologies;
    },
    queryKey:
      studyId && taskId ? queryKeys.meia.getTaskTechnologies({ studyId, taskId }) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaTasksTechnologies = (params: {
  enabled?: boolean;
  studyId?: string;
  tasks: StudyTaskSelectDto[];
}): MeiaTasksTechnologiesResult => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, tasks } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId);

  const queries = useQueries({
    queries: tasks.map((task) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTaskTechnologiesSelectDto>({
          path: `/v1/intel/studies/${studyId}/tasks/${task.id}/technologies`,
        });
        return data.taskTechnologies;
      },
      queryKey: enabled
        ? queryKeys.meia.getTaskTechnologies({ studyId: studyId ?? '', taskId: task.id })
        : queryKeys.placeholders.default,
    })),
  });

  return {
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    taskTechnologies: queries.flatMap((query) => query.data ?? []),
  };
};

export const useGetMeiaTaskAttachableTechnologies = (params: {
  enabled?: boolean;
  limit?: number;
  page?: number;
  search?: string | null;
  studyId?: string;
  taskId?: string;
}) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, limit = 25, page = 1, search, studyId, taskId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId && taskId);
  const queryParams = {
    limit,
    page,
    search: search?.trim() || undefined,
    studyId,
    taskId,
  };

  return useQuery<IntelTechnologiesSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<IntelTechnologiesSelectDto>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/attachable-technologies`,
        query: {
          limit,
          page,
          search: search?.trim() || undefined,
        },
      });
    },
    queryKey: enabled ? queryKeys.meia.getTaskAttachableTechnologies(queryParams) : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaTaskTechnology = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyTaskTechnologySelectDto,
    Error,
    { studyId: string; taskId: string } & StudyTaskTechnologyCreateDto
  >({
    mutationFn: async ({ studyId, taskId, ...formData }) => {
      return await apiPost<StudyTaskTechnologySelectDto>({
        body: formData,
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/technologies`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meia', 'getTaskAttachableTechnologies'], refetchType: 'all' });
      queryClient.invalidateQueries({
        queryKey: queryKeys.meia.getTaskTechnologies({ studyId: variables.studyId, taskId: variables.taskId }),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaTaskTechnology = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { studyId: string; taskId: string; taskTechnologyId: string }>({
    mutationFn: async ({ studyId, taskTechnologyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/task-technologies/${taskTechnologyId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meia', 'getTaskAttachableTechnologies'], refetchType: 'all' });
      queryClient.invalidateQueries({
        queryKey: queryKeys.meia.getTaskTechnologies({ studyId: variables.studyId, taskId: variables.taskId }),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaTaskTechnologyEngagements = (params: {
  enabled?: boolean;
  studyId?: string;
  taskTechnologyId?: string;
}) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, taskTechnologyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId && taskTechnologyId);

  return useQuery<StudyTaskTargetEngagementSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskTargetEngagementsSelectDto>({
        path: `/v1/intel/studies/${studyId}/task-technologies/${taskTechnologyId}/engagements`,
      });
      return data.engagements;
    },
    queryKey:
      enabled && taskTechnologyId
        ? queryKeys.meia.getTaskTechnologyEngagements(taskTechnologyId)
        : queryKeys.placeholders.default,
  });
};

export const useGetMeiaTaskTechnologiesEngagements = (params: {
  enabled?: boolean;
  studyId?: string;
  taskTechnologies: StudyTaskTechnologySelectDto[];
}): MeiaTaskTechnologiesEngagementsResult => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, taskTechnologies } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId);

  const queries = useQueries({
    queries: taskTechnologies.map((taskTechnology) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTaskTargetEngagementsSelectDto>({
          path: `/v1/intel/studies/${studyId}/task-technologies/${taskTechnology.id}/engagements`,
        });
        return data.engagements;
      },
      queryKey: enabled
        ? queryKeys.meia.getTaskTechnologyEngagements(taskTechnology.id)
        : queryKeys.placeholders.default,
    })),
  });

  return {
    engagements: queries.flatMap((query) => query.data ?? []),
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
  };
};

/**
 * Batch load of every target engagement for a study in one request. Study-wide
 * surfaces (P5 brief, task canvas columns/cards) share this single query instead
 * of fanning out one request per task-technology.
 */
export const useGetMeiaStudyTargetEngagements = (params: { enabled?: boolean; studyId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId);

  return useQuery<StudyTaskTargetEngagementSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskTargetEngagementsSelectDto>({
        path: `/v1/intel/studies/${studyId}/target-engagements`,
      });
      return data.engagements;
    },
    queryKey: enabled ? queryKeys.meia.getStudyTargetEngagements(studyId ?? '') : queryKeys.placeholders.default,
  });
};

export const useCreateMeiaTaskTechnologyEngagement = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyTaskTargetEngagementSelectDto,
    Error,
    { studyId: string; taskTechnologyId: string } & StudyTaskTargetEngagementCreateDto
  >({
    mutationFn: async ({ studyId, taskTechnologyId, ...body }) => {
      return await apiPost<StudyTaskTargetEngagementSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/task-technologies/${taskTechnologyId}/engagements`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.meia.getTaskTechnologyEngagements(variables.taskTechnologyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyTargetEngagements(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useUpdateMeiaTaskTechnologyEngagement = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyTaskTargetEngagementSelectDto,
    Error,
    { engagementId: string; studyId: string; taskTechnologyId: string } & StudyTaskTargetEngagementUpdateDto
  >({
    mutationFn: async ({ engagementId, studyId, taskTechnologyId: _taskTechnologyId, ...body }) => {
      return await apiPatch<StudyTaskTargetEngagementSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/task-target-engagements/${engagementId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.meia.getTaskTechnologyEngagements(variables.taskTechnologyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyTargetEngagements(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useDeleteMeiaTaskTechnologyEngagement = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { engagementId: string; studyId: string; taskTechnologyId: string }>({
    mutationFn: async ({ engagementId, studyId }) => {
      await apiDelete<void>({
        path: `/v1/intel/studies/${studyId}/task-target-engagements/${engagementId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.meia.getTaskTechnologyEngagements(variables.taskTechnologyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyTargetEngagements(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaThreadTaskReadiness(queryClient);
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaTargetEffectOverrides = (params: { studyId?: string }) => {
  const { apiGet } = useApi();
  const { studyId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  return useQuery<StudyTargetEffectOverrideSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTargetEffectOverridesSelectDto>({
        path: `/v1/intel/studies/${studyId}/target-effect-overrides`,
      });
      return data.targetEffectOverrides;
    },
    queryKey: enabled && studyId ? queryKeys.meia.getTargetEffectOverrides(studyId) : queryKeys.placeholders.default,
  });
};

export const useUpsertMeiaTargetEffectOverride = () => {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    StudyTargetEffectOverrideSelectDto,
    Error,
    { studyId: string } & StudyTargetEffectOverrideUpsertDto
  >({
    mutationFn: async ({ studyId, ...body }) => {
      return await apiPut<StudyTargetEffectOverrideSelectDto>({
        body,
        path: `/v1/intel/studies/${studyId}/target-effect-overrides`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTargetEffectOverrides(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useClearMeiaTargetEffectOverride = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { studyId: string } & StudyTargetEffectOverrideClearDto>({
    mutationFn: async ({ studyId, ...body }) => {
      await apiDelete<void>({
        body,
        path: `/v1/intel/studies/${studyId}/target-effect-overrides`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getTargetEffectOverrides(variables.studyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudyP4E2eRows(variables.studyId) });
      invalidateMeiaGateQueries(queryClient);
    },
  });
};

export const useGetMeiaTaskMoes = (params: { enabled?: boolean; studyId?: string; taskId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, taskId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId && taskId);

  return useQuery<StudyTaskMoeSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskMoesSelectDto>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/moes`,
      });
      return data.taskMoes;
    },
    queryKey: taskId ? queryKeys.meia.getTaskMoes(taskId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaTaskMops = (params: { enabled?: boolean; studyId?: string; taskId?: string }) => {
  const { apiGet } = useApi();
  const { enabled: enabledOverride = true, studyId, taskId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(enabledOverride && isAuthenticated && studyId && taskId);

  return useQuery<StudyTaskMopSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskMopsSelectDto>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/mops`,
      });
      return data.taskMops;
    },
    queryKey: taskId ? queryKeys.meia.getTaskMops(taskId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaTaskMoses = (params: { studyId?: string; taskId?: string }) => {
  const { apiGet } = useApi();
  const { studyId, taskId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId && taskId);

  return useQuery<StudyTaskMosSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskMosesSelectDto>({
        path: `/v1/intel/studies/${studyId}/tasks/${taskId}/moses`,
      });
      return data.taskMoses;
    },
    queryKey: enabled && taskId ? queryKeys.meia.getTaskMoses(taskId) : queryKeys.placeholders.default,
  });
};

export type MeiaThreadMeasureRow = {
  description: string;
  /** Stable identity for list keys (MoS/MoE id; MoP rows use `mopId#swimlaneId`). */
  id: string;
  swimlaneId: string;
};

export type MeiaThreadMeasureRollup = {
  isLoading: boolean;
  moe: MeiaThreadMeasureRow[];
  mop: MeiaThreadMeasureRow[];
  mos: MeiaThreadMeasureRow[];
};

// The rollup fan-out re-runs on every P2 mount/focus with the client's default staleTime of 0;
// a short staleTime keeps navigation from re-firing the whole burst while staying fresh enough
// for authoring. Mutations still invalidate these keys directly.
const MEASURE_ROLLUP_STALE_TIME_MS = 60_000;

/**
 * Rolls every Measure of Success / Effectiveness / Performance authored anywhere in a Thread's
 * Stages up into three flat, stage-tagged lists for the P2 thread-measures tiles. MoS come straight
 * off each Stage; MoE fan out from their parent MoS (inheriting the Stage); MoP are mission-level
 * entries reached through the Tasks they are bound to, tagged with the Task's Stage and de-duped so a
 * MoP bound to several Tasks in the same Stage is listed once.
 */
export const useGetMeiaThreadMeasureRollup = (params: {
  missionId?: string;
  studyId?: string;
  swimlanes: StudySwimlaneSelectDto[];
}): MeiaThreadMeasureRollup => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { missionId, studyId, swimlanes } = params;
  const enabled = Boolean(isAuthenticated && studyId);

  const mosQueries = useQueries({
    queries: swimlanes.map((swimlane) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMosEntriesSelectDto>({ path: `/v1/intel/swimlanes/${swimlane.id}/mos-entries` });
        return data.mosEntries;
      },
      queryKey: enabled ? queryKeys.meia.getSwimlaneMosEntries(swimlane.id) : queryKeys.placeholders.default,
      staleTime: MEASURE_ROLLUP_STALE_TIME_MS,
    })),
  });

  const mosRows = swimlanes.flatMap((swimlane, index) =>
    (mosQueries[index]?.data ?? []).map((mos) => ({
      description: mos.description,
      id: mos.id,
      swimlaneId: swimlane.id,
    })),
  );

  const moeQueries = useQueries({
    queries: mosRows.map((mos) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyMoeEntriesSelectDto>({ path: `/v1/intel/mos-entries/${mos.id}/moe-entries` });
        return data.moeEntries;
      },
      queryKey: enabled ? queryKeys.meia.getMoeEntries(mos.id) : queryKeys.placeholders.default,
      staleTime: MEASURE_ROLLUP_STALE_TIME_MS,
    })),
  });

  const taskQueries = useQueries({
    queries: swimlanes.map((swimlane) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTasksSelectDto>({
          path: `/v1/intel/studies/${studyId}/swimlanes/${swimlane.id}/tasks`,
        });
        return data.tasks;
      },
      queryKey: enabled ? queryKeys.meia.getSwimlaneTasks(swimlane.id) : queryKeys.placeholders.default,
      staleTime: MEASURE_ROLLUP_STALE_TIME_MS,
    })),
  });

  const tasks = taskQueries.flatMap((query) => query.data ?? []);

  const taskMopQueries = useQueries({
    queries: tasks.map((task) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTaskMopsSelectDto>({
          path: `/v1/intel/studies/${studyId}/tasks/${task.id}/mops`,
        });
        return data.taskMops;
      },
      queryKey: enabled ? queryKeys.meia.getTaskMops(task.id) : queryKeys.placeholders.default,
      staleTime: MEASURE_ROLLUP_STALE_TIME_MS,
    })),
  });

  const { data: mopEntries = [], isLoading: isMopEntriesLoading } = useGetMeiaMopEntries({ missionId });

  const mos: MeiaThreadMeasureRow[] = mosRows.map((row) => ({
    description: row.description,
    id: row.id,
    swimlaneId: row.swimlaneId,
  }));

  const moe: MeiaThreadMeasureRow[] = mosRows.flatMap((row, index) =>
    (moeQueries[index]?.data ?? []).map((entry) => ({
      description: entry.description,
      id: entry.id,
      swimlaneId: row.swimlaneId,
    })),
  );

  const mopLabelById = new Map(mopEntries.map((entry) => [entry.id, entry.name || entry.description]));
  const seenMop = new Set<string>();
  const mop: MeiaThreadMeasureRow[] = [];
  tasks.forEach((task, index) => {
    for (const link of taskMopQueries[index]?.data ?? []) {
      const dedupeKey = `${link.studyMopId}#${task.swimlaneId}`;
      if (seenMop.has(dedupeKey)) continue;
      seenMop.add(dedupeKey);
      mop.push({
        description: mopLabelById.get(link.studyMopId) ?? 'Untitled Measure of Performance',
        id: dedupeKey,
        swimlaneId: task.swimlaneId,
      });
    }
  });

  const isLoading =
    mosQueries.some((query) => query.isLoading) ||
    moeQueries.some((query) => query.isLoading) ||
    taskQueries.some((query) => query.isLoading) ||
    taskMopQueries.some((query) => query.isLoading) ||
    isMopEntriesLoading;

  return { isLoading, moe, mop, mos };
};

export const useGetMeiaThreadTaskReadiness = (params: { studyId?: string; threadId?: string }) => {
  const { apiGet } = useApi();
  const { studyId, threadId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId && threadId);

  return useQuery<StudyTaskReadinessSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<StudyTaskReadinessListSelectDto>({
        path: `/v1/intel/studies/${studyId}/threads/${threadId}/task-readiness`,
      });
      return data.taskReadiness;
    },
    queryKey: enabled && threadId ? queryKeys.meia.getThreadTaskReadiness(threadId) : queryKeys.placeholders.default,
  });
};

export const useGetMeiaStudyThreadTaskReadiness = (params: {
  studyId?: string;
  threads: StudyThreadSelectDto[];
}): MeiaStudyThreadTaskReadinessResult => {
  const { apiGet } = useApi();
  const { studyId, threads } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && studyId);

  const queries = useQueries({
    queries: threads.map((thread) => ({
      enabled,
      queryFn: async () => {
        const data = await apiGet<StudyTaskReadinessListSelectDto>({
          path: `/v1/intel/studies/${studyId}/threads/${thread.id}/task-readiness`,
        });
        return data.taskReadiness;
      },
      queryKey: enabled ? queryKeys.meia.getThreadTaskReadiness(thread.id) : queryKeys.placeholders.default,
    })),
  });

  return {
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    taskReadiness: queries.flatMap((query, index) => {
      const thread = threads[index];
      if (!thread) return [];
      return (query.data ?? []).map((entry) => ({ ...entry, threadId: thread.id }));
    }),
  };
};
